import { useCallback } from 'react';
import {
  DragDropContext,
  Droppable,
  DropResult,
  DroppableProvided,
} from 'react-beautiful-dnd';
import { useFlags } from 'launchdarkly-react-client-sdk';
import intl from 'react-intl-universal';
import {
  Table,
  TableHead,
  TableOperationHeader,
  Typography,
  Button,
  tailwindOverride,
} from '@getsynapse/design-system';
import TaskTemplateModal from '../TaskTemplateModal/TaskTemplateModal';
import { DeleteTaskConfirmationModal } from '../DeleteTaskConfirmationModal';
import EmptyTasksTable from '../EmptyTasksTable';
import TaskTemplateRow from './TaskTemplateRow';
import { useTaskBundleDraft } from '../../providers/TaskBundleProvider/TaskBundleProvider';
import { useTemplateSearch } from './hooks/useTemplateSearch';
import { useTemplateModal } from './hooks/useTemplateModal';
import { useTemplateDelete } from './hooks/useTemplateDelete';

type BuildBundleProps = {
  isEditing?: boolean;
};

const BuildBundle = ({ isEditing = false }: BuildBundleProps) => {
  const {
    newTaskTypes = false,
    taskDependenciesInBundles = false,
    relativeDates = false,
  } = useFlags();
  const showDependencies = newTaskTypes && taskDependenciesInBundles;
  const showTimeline = newTaskTypes && relativeDates;
  const { selectors, actions } = useTaskBundleDraft();
  const templates = selectors.templates;

  const { searchParamValue, handleSearch, formattedTemplates } =
    useTemplateSearch(templates);
  const {
    activeTask,
    isTaskTemplateModalOpen,
    toggleTaskTemplateModal,
    openTaskForEdit,
    closeModal,
  } = useTemplateModal();
  const { taskToDelete, setTaskToDelete, confirmDelete } = useTemplateDelete();

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;
      if (result.destination.index === result.source.index) return;
      actions.reorderTemplates(result.source.index, result.destination.index);
    },
    [actions]
  );

  const isTemplatesTableEmpty = templates.length === 0;

  return (
    <div className='flex flex-col'>
      <div className='my-4 flex justify-between'>
        <Typography
          variant='body'
          className='text-neutral-dark text-lg font-normal'
        >
          {intl.get('SETTINGS_PAGE.TASK_BUNDLE_PAGE.BUILD_SECTION.TASK_LIST')}
        </Typography>

        <Button
          onClick={toggleTaskTemplateModal}
          className='px-4 py-2'
          data-testid='add-task-template-button'
          size='small'
          aria-label='add-task-template-button'
        >
          {intl.get('TASKS.ADD_TASK_BUTTON_TITLE')}
        </Button>
      </div>
      {isEditing && (
        <TableOperationHeader
          className='border-0 rounded-b-none bg-neutral-white'
          inputValue={searchParamValue}
          setInputValue={handleSearch}
          searchProps={{
            disabled: isTemplatesTableEmpty,
            placeholder:
              !isTemplatesTableEmpty && intl.get('SEARCH_PLACEHOLDER'),
          }}
        />
      )}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div
          className={tailwindOverride(
            'w-full overflow-auto',
            { 'max-h-task-bundles-table-body': isEditing },
            { 'max-h-filtered-table-body': !isEditing }
          )}
          id='task-templates-table-scroll'
        >
          <Table
            className='w-full mb-1 relative table-fixed'
            canSelectRows={false}
            data={{ headData: { headCells: [] }, rows: [] }}
            aria-label='task-templates-table'
          >
            <TableHead
              stickyHeader
              headCells={[
                ...(newTaskTypes
                  ? [
                      {
                        content: '',
                        className: tailwindOverride('h-full w-6', {
                          'z-2 py-3 px-0': !isTemplatesTableEmpty,
                        }),
                      },
                    ]
                  : []),
                {
                  content: (
                    <>
                      {intl.get(
                        'SETTINGS_PAGE.TASK_BUNDLE_PAGE.BUILD_SECTION.NAME'
                      )}
                      <span className='text-error-dark'>*</span>
                    </>
                  ),
                  className: 'font-semibold py-3 px-4 w-5/12',
                },
                {
                  content: intl.get(
                    'SETTINGS_PAGE.TASK_BUNDLE_PAGE.BUILD_SECTION.DESCRIPTION'
                  ),
                  className: 'font-semibold py-3 px-4 w-7/12',
                },
                {
                  content: intl.get('TASK.TASK_TYPE'),
                  className: 'font-semibold py-3 px-4 w-46',
                },
                ...(showDependencies
                  ? [
                      {
                        content: intl.get(
                          'SETTINGS_PAGE.TASK_BUNDLE_PAGE.DEPENDENCIES.RELATIONSHIP_DEPENDS_ON'
                        ),
                        className: 'font-semibold py-3 px-4 w-52',
                      },
                    ]
                  : []),
                {
                  content: intl.get(
                    'SETTINGS_PAGE.TASK_BUNDLE_PAGE.TABLE.HEAD.ESTIMATED_HOURS'
                  ),
                  className: 'font-semibold py-3 px-4 w-28',
                },
                ...(showTimeline
                  ? [
                      {
                        content: intl.get(
                          'SETTINGS_PAGE.TASK_BUNDLE_PAGE.BUILD_SECTION.TIMELINE.TITLE'
                        ),
                        className: 'font-semibold py-3 px-4 w-52',
                      },
                    ]
                  : []),
                {
                  content: <div />,
                  className: tailwindOverride(
                    'h-full bg-primary-lightest z-2',
                    {
                      'w-16 right-0': !isTemplatesTableEmpty,
                    }
                  ),
                },
              ]}
            />
            <Droppable droppableId='task-templates-droppable'>
              {(droppableProvided: DroppableProvided) => (
                <tbody
                  ref={droppableProvided.innerRef}
                  {...droppableProvided.droppableProps}
                >
                  {formattedTemplates.length === 0 && searchParamValue ? (
                    <tr className='cursor-auto pointer-events-none text-label text-primary'>
                      <td className='border-b border-t border-r-0 border-transparent w-5/12 group-hover:shadow-first-table-cell-hover' />
                      <td className='border-b border-t border-r-0 border-transparent px-4 py-3 w-7/12 group-hover:border-secondary-darker shadow-table-column group-hover:shadow-none'>
                        <Typography
                          variant='body2'
                          className='leading-4 text-primary-light'
                        >
                          {intl.get(
                            'SETTINGS_PAGE.TASK_BUNDLE_PAGE.BUILD_SECTION.NO_RESULTS_FOUND'
                          )}
                        </Typography>
                      </td>
                      <td className='border-b border-t border-r border-transparent w-16 right-0 sticky z-1' />
                    </tr>
                  ) : (
                    formattedTemplates.map((taskTemplate, index) => (
                      <TaskTemplateRow
                        key={taskTemplate.id}
                        taskTemplate={taskTemplate}
                        index={index}
                        isOdd={index % 2 !== 0}
                        onEdit={openTaskForEdit}
                        onDelete={setTaskToDelete}
                      />
                    ))
                  )}
                  {droppableProvided.placeholder}
                </tbody>
              )}
            </Droppable>
          </Table>
        </div>
      </DragDropContext>
      <DeleteTaskConfirmationModal
        isOpen={taskToDelete !== null}
        closeModal={() => setTaskToDelete(null)}
        onCancel={() => setTaskToDelete(null)}
        onDeleteTask={confirmDelete}
      />
      {!searchParamValue && formattedTemplates.length === 0 && (
        <EmptyTasksTable>
          <Button
            onClick={toggleTaskTemplateModal}
            className='px-4 py-2'
            data-testid='add-task-template-button'
            size='small'
            aria-label='empty-tasks-table-add-task-button'
          >
            {intl.get('TASKS.ADD_TASK_BUTTON_TITLE')}
          </Button>
        </EmptyTasksTable>
      )}
      <TaskTemplateModal
        mode={activeTask ? 'edit' : 'create'}
        isOpen={isTaskTemplateModalOpen}
        onClose={closeModal}
        taskTemplateData={activeTask ?? undefined}
      />
    </div>
  );
};

export default BuildBundle;
